import { useEffect, useState } from "react";
import { fileUrl } from "@/lib/api";
import {
  getPreweddingSessions, savePreweddingSession, deletePreweddingSession,
  normalizeGallery,
} from "@/lib/cloudApi";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, ArrowLeft, Save, Loader2 } from "lucide-react";
import CloudImagePicker from "@/components/admin/CloudImagePicker";
import MultiImagePicker from "@/components/admin/MultiImagePicker";

const empty = {
  slug: "", couple: "", location: "", date: "", type: "Pre-Wedding",
  cover: "", story: "", fullStory: "", gallery: [], order: 0,
  featured: false, status: "published",
  coverPositionDesktop: "center center",
  coverPositionMobile: "center center",
  coverFit: "cover",
};

const coverPositions = [
  ["Top center", "center top"],
  ["Center center", "center center"],
  ["Upper third (face-safe)", "center 30%"],
  ["Bottom center", "center bottom"],
];

export default function AdminPreWedding() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("list"); // list | form
  const [form, setForm] = useState(empty);
  const [editingSlug, setEditingSlug] = useState(null);
  const [busy, setBusy] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const data = await getPreweddingSessions(); setItems(Array.isArray(data) ? data : []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const startNew = () => { setForm(empty); setEditingSlug(null); setMode("form"); };
  const startEdit = (s) => {
    setForm({ ...empty, ...s, gallery: normalizeGallery(s.gallery) });
    setEditingSlug(s.slug);
    setMode("form");
  };
  const cancel = () => { setMode("list"); setForm(empty); setEditingSlug(null); };

  const del = async (slug) => {
    if (!window.confirm(`Delete pre-wedding session "${slug}"?`)) return;
    try { await deletePreweddingSession(slug); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (galleryUploading) { toast.error("Please wait for the gallery upload to finish"); return; }
    if (!form.slug.match(/^[a-z0-9-]+$/)) {
      toast.error("Slug must be lowercase letters / numbers / hyphens only");
      return;
    }
    setBusy(true);
    try {
      await savePreweddingSession({ ...form, gallery: normalizeGallery(form.gallery) });
      toast.success(editingSlug ? "Session updated" : "Session created");
      await load();
      cancel();
    } catch (err) {
      toast.error("Save failed");
    } finally { setBusy(false); }
  };


  // ── LIST VIEW ───────────────────────────────────────────────
  if (mode === "list") {
    return (
      <div className="p-10" data-testid="admin-prewedding">
        <div className="flex items-center justify-between">
          <div>
            <div className="eyebrow text-[#C99A45]">Manage</div>
            <h1 className="font-serif-display text-5xl mt-3">Pre-Wedding Sessions</h1>
          </div>
          <button onClick={startNew} className="btn-gold flex items-center gap-2" data-testid="add-prewedding-session">
            <Plus size={14} /> Add Session
          </button>
        </div>
        <div className="gold-divider my-6" />

        {loading ? (
          <div className="text-[#F7F1E8]/60 mt-10">Loading...</div>
        ) : items.length === 0 ? (
          <div className="mt-10 border border-dashed border-[#C99A45]/25 p-10 text-center">
            <p className="text-[#F7F1E8]/55 text-sm">No pre-wedding sessions yet.</p>
            <p className="text-[#F7F1E8]/35 text-xs mt-2">
              Click “Add Session” to create your first couple/session album.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {items.map((s) => {
              const gallery = normalizeGallery(s.gallery);
              const preview = s.cover || gallery[0] || "";
              return (
                <div key={s.slug} className="border border-[#C99A45]/20 p-4 group" data-testid={`pw-session-${s.slug}`}>
                  <div className="aspect-[4/3] bg-[#151515] overflow-hidden">
                    {preview && (
                      <img src={fileUrl(preview)} alt={s.couple || "Pre-wedding"}
                        className="w-full h-full object-cover"
                        style={{ objectPosition: s.coverPositionDesktop && s.coverPositionDesktop !== "center center" ? s.coverPositionDesktop : "center 30%" }} />
                    )}
                  </div>
                  <div className="mt-4">
                    <div className="eyebrow text-[10px] text-[#C99A45]/80">
                      {[s.type, s.location].filter(Boolean).join(" · ")}{s.status === "draft" ? " · DRAFT" : ""}
                    </div>
                    <h3 className="font-serif-display text-2xl mt-1">{s.couple}</h3>
                    <div className="eyebrow text-[9px] text-[#F7F1E8]/50 mt-1">/{s.slug} · {gallery.length} photos</div>
                    <div className="flex gap-2 mt-4">
                      <button onClick={() => startEdit(s)} className="btn-outline-gold !py-2 !px-3 text-xs flex-1 flex items-center justify-center gap-1" data-testid={`edit-pw-${s.slug}`}>
                        <Edit3 size={12} /> Edit
                      </button>
                      <button onClick={() => del(s.slug)} className="px-3 py-2 border border-[#E52717]/50 text-[#E52717] text-xs hover:bg-[#E52717] hover:text-white transition" data-testid={`del-pw-${s.slug}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── FORM VIEW ───────────────────────────────────────────────
  return (
    <div className="p-6 md:p-10 max-w-4xl" data-testid="admin-prewedding-form">
      <button onClick={cancel} className="admin-button-secondary !py-2 !px-4 mb-5">
        <ArrowLeft size={12} /> Back to sessions
      </button>
      <h1 className="font-serif-display text-3xl md:text-4xl text-[#171717]">
        {editingSlug ? "Edit Pre-Wedding Session" : "New Pre-Wedding Session"}
      </h1>
      <p className="text-sm text-[#6f6a62] mt-2">
        Create a named pre-wedding album that appears on the public Pre-Wedding page.
      </p>

      <form onSubmit={submit} className="mt-8">
        {/* DETAILS */}
        <section className="admin-form-section">
          <h2 className="admin-form-section-title">Basic Details</h2>
          <div className="admin-grid-2">
            <div className="admin-field">
              <label className="admin-label">Couple / Session name <span className="req">*</span></label>
              <input value={form.couple} onChange={e => set("couple", e.target.value)} placeholder="Aarav & Nidhi" />
            </div>
            <div className="admin-field">
              <label className="admin-label">Slug (URL) <span className="req">*</span></label>
              <input value={form.slug} onChange={e => set("slug", e.target.value)} placeholder="aarav-nidhi" />
              <p className="admin-help">Lowercase letters, numbers and hyphens only. Used in /pre-wedding/your-slug</p>
            </div>
            <div className="admin-field">
              <label className="admin-label">Location</label>
              <input value={form.location} onChange={e => set("location", e.target.value)} placeholder="Udaipur" />
            </div>
            <div className="admin-field">
              <label className="admin-label">Date</label>
              <input value={form.date} onChange={e => set("date", e.target.value)} placeholder="March 2025" />
            </div>
          </div>
          <div className="admin-field">
            <label className="admin-label">Short story</label>
            <input value={form.story} onChange={e => set("story", e.target.value)} placeholder="One line shown on the listing card" />
          </div>
          <div className="admin-field">
            <label className="admin-label">Full story</label>
            <textarea rows={4} value={form.fullStory} onChange={e => set("fullStory", e.target.value)} placeholder="The full story shown on the session detail page" />
          </div>
          <div className="admin-grid-3">
            <div className="admin-field">
              <label className="admin-label">Order</label>
              <input type="number" value={form.order} onChange={e => set("order", Number(e.target.value))} />
            </div>
            <div className="admin-field">
              <label className="admin-label">Status</label>
              <select value={form.status} onChange={e => set("status", e.target.value)}>
                <option value="published">Published</option>
                <option value="draft">Draft (hidden)</option>
              </select>
            </div>
            <div className="admin-field flex items-end">
              <label className="flex items-center gap-2 text-sm text-[#3a352d] cursor-pointer pb-2">
                <input type="checkbox" className="!w-auto" checked={form.featured} onChange={e => set("featured", e.target.checked)} />
                Featured
              </label>
            </div>
          </div>
        </section>

        {/* COVER */}
        <section className="admin-form-section">
          <h2 className="admin-form-section-title">Cover Image</h2>
          <CloudImagePicker value={form.cover} onChange={v => set("cover", v)} label="Cover image" testId="pw-cover" folder="prewedding" />
          <div className="admin-grid-2 mt-4">
            <div className="admin-field">
              <label className="admin-label">Cover position (desktop)</label>
              <select value={form.coverPositionDesktop} onChange={e => set("coverPositionDesktop", e.target.value)}>
                {coverPositions.map(([label, val]) => <option key={val} value={val}>{label}</option>)}
              </select>
            </div>
            <div className="admin-field">
              <label className="admin-label">Cover fit</label>
              <select value={form.coverFit} onChange={e => set("coverFit", e.target.value)}>
                <option value="cover">Cover (fill)</option>
                <option value="contain">Contain (full image)</option>
              </select>
            </div>
          </div>
        </section>

        {/* GALLERY */}
        <section className="admin-form-section">
          <h2 className="admin-form-section-title">Album Gallery</h2>
          <MultiImagePicker
            urls={form.gallery}
            onChange={(urls) => { set("gallery", urls); }}
            label="Session photos"
            testId="pw-gallery"
            allowDriveFolderImport
          />
          <p className="admin-help mt-2">{normalizeGallery(form.gallery).length} photos in this album.</p>
        </section>

        <div className="admin-form-actions">
          <button type="submit" disabled={busy} className="admin-button-primary" data-testid="pw-save">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            {editingSlug ? "Update Session" : "Create Session"}
          </button>
          <button type="button" onClick={cancel} className="admin-button-secondary">Cancel</button>
        </div>
      </form>
    </div>
  );
}
